import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { AuthService } from "app/auth/auth.service";
import { CompanyManagementService } from "providers/company-management-service/company-management-service";
import { UsersService } from "providers/users-service/users.service";
import { AccessControlService } from "../../../../app/access-control/access-control-service";
import { Workforce } from "../../../../app/access-control/permissions";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import { LookupValue } from "../../../../models/common/lookup-value";
import ResourceManagementLookups from "../../../../models/common/resource-management-lookups";
import Company from "../../../../models/company-details/company";
import Role from "../../../../models/system-admin/roles";
import { commonVar } from "../../../../providers/common-var/common";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { RolesService } from "../../../../providers/roles/roles.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { WorkforceContactDetails } from "../workforce-contact-details/workforce-contact-details";

const ENTITY_TYPE = "ROLE";
const COMPANY_ID_VALUE = "000000";
const COMPANY_ID_FIELD = "companyId";

// OPS-2858 Account Setup defaults. Ids mirror the resource-management enums:
// Department.ADMINISTRATION(13, "Administrative"), Position.ADMINISTRATOR(903),
// DisciplineCode.ND(17, "No Discipline").
const SUPPORT_USER_DEFAULT_DEPARTMENT = 13;
const SUPPORT_USER_DEFAULT_POSITION = 903;
const SUPPORT_USER_DEFAULT_DISCIPLINE = 17;
const SUPPORT_USER_DEFAULT_ROLE_NAME = "Administrator";

@Component({
  selector: "workforce-account-contact-details",
  templateUrl: "workforce-account-contact-details.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkforceAccountContactDetails),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WorkforceAccountContactDetails),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkforceAccountContactDetails
  extends WorkforceContactDetails
  implements OnInit
{
  /**
   * OPS-2858. Support Users tab only. Drives the Account Setup defaults below.
   */
  @Input() isSupportUser: boolean = false;

  @Input() isEditMode: boolean = false;
  @Input() isNew: boolean = false;
  @Input() company: Company | undefined;

  departmentList: LookupValue[] = [];
  disciplineList: LookupValue[] = [];
  positionList: LookupValue[] = [];
  selectorOpen: boolean = false;
  emailAddress: string = "";
  public rolesData: Role[] = [];
  private filterParameters: any = {};
  private _rmLookups: ResourceManagementLookups;
  _permissionsAccess = {} as PermissionsResponse;
  hasAccessToAccountSetup: boolean = false;

  @Input()
  set permissionsAccess(permissionsAccess: PermissionsResponse) {
    if (
      permissionsAccess &&
      permissionsAccess.permissions &&
      permissionsAccess.permissions.length > 0
    ) {
      this._permissionsAccess = permissionsAccess;
      this.checkCognitoUserPermissions();
    }
  }
  @Input()
  set rmLookups(rmLookups: ResourceManagementLookups) {
    this._rmLookups = rmLookups;
    if (rmLookups && rmLookups.Position && rmLookups.Position.length > 0) {
      this.mapLookups(rmLookups);
    }
  }

  get rmLookups() {
    return this._rmLookups;
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected resourceManagementService: ResourceManagementService,
    protected companyManagementService: CompanyManagementService,
    protected usersService: UsersService,
    protected authService: AuthService,
    private utilityService: UtilityService,
    private roleService: RolesService,
    private accessControlService: AccessControlService
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      id: [null],
      accountName: [
        "",
        [
          Validators.required,
          Validators.pattern(commonVar.EMAIL_REGEX),
          Validators.maxLength(50),
        ],
      ],
      position: [null, [Validators.required]],
      department: [null, [Validators.required]],
      employeeAccountDetails: [[], [Validators.required]],
      employeeAccountDisciplines: [[], [Validators.required]],
      cognitoUserId: [null],
    });

    //Patch Select Value from RM Service
    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );
  }

  ngOnInit() {
    if (this.company) {
      this.loadRoles();
    }
    this.resourceManagementService.systemUserEmailAddress.subscribe((email) => {
      this.accountName.setValue(email);
      this.emailAddress = email;
    });
    this.innerFormGroup.markAsTouched();
  }

  ngAfterViewInit() {
    this.accountName.setValue(this.emailAddress);
    this.innerFormGroup.markAsTouched();
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid ? null : { account: { valid: false } };
  }

  get accountName() {
    return this.innerFormGroup.get("accountName") as FormControl;
  }

  private mapLookups(rmLookups: ResourceManagementLookups) {
    this.departmentList = rmLookups["Department"].map((item) => {
      return {
        description: item.description,
        id: item.id,
      };
    });
    this.positionList = rmLookups["Position"].map((item) => {
      return {
        description: item.description,
        id: item.id,
      };
    });
    this.disciplineList = rmLookups["DisciplineName"].map((item) => {
      return {
        description: item.description,
        id: item.id,
      };
    });
  }

  private checkEmployeeAccountDetails() {
    if (
      this.innerFormGroup.get("employeeAccountDetails") &&
      this.innerFormGroup.get("employeeAccountDetails").value
    ) {
      this.innerFormGroup
        .get("employeeAccountDetails")
        .setValue(this.innerFormGroup.get("employeeAccountDetails").value);
      this.innerFormGroup.get("employeeAccountDetails").markAsTouched();
    }
  }

  loadRoles() {
    try {
      this.utilityService.increaseSpinnerCount("Loading Roles...");
      this.rolesData = [];
      this.loadFilters();

      this.roleService.getUsersRoles(this.filterParameters).subscribe(
        (response) => {
          this.rolesData = [];
          if (response && response["hits"] && response["totalHits"] > 0) {
            this.rolesData = response["hits"] as Role[];
          }
        },
        (err) => {
          this.utilityService.decreaseSpinnerCount();
          let msg = "Error loading roles.";
          this.utilityService.presentErrorToast(err, msg);
        },
        () => {
          this.innerFormGroup.markAsTouched();
          this.checkEmployeeAccountDetails();
          this.applySupportUserAccountDefaults();
          this.utilityService.decreaseSpinnerCount();
        }
      );
    } catch (e) {
      console.log("An error has occurred: ", e);
      this.utilityService.decreaseSpinnerCount();
    }
  }

  loadFilters() {
    this.filterParameters = {};
    this.filterParameters.entityType = ENTITY_TYPE;
    this.filterParameters.from = 0;
    this.filterParameters.size = 50;
    this.filterParameters.optionalFields = [
      {
        name: COMPANY_ID_FIELD,
        value: COMPANY_ID_VALUE,
      },
      {
        name: COMPANY_ID_FIELD,
        value: this.company.companyId,
      },
    ];
  }

  checkCognitoUserPermissions() {
    this.hasAccessToAccountSetup = this.accessControlService.hasAccess(
      this._permissionsAccess,
      Workforce.CompanyWorkforceAll
    );
  }

  /**
   * OPS-2858. Account Setup defaults for a support user. All EDITABLE — the admin can
   * change any of them; they are a starting point, not a constraint.
   *
   * Two of these are load-bearing rather than cosmetic. UserProfileModel declares
   * Department as `validate:"required"` and Disciplines as `validate:"required,min=1"`,
   * so a support user saved without them is rejected by user-profile-functions with a
   * 400 that surfaces nowhere useful. "No Discipline" (ND) exists precisely so the
   * min=1 can be satisfied by someone who has none.
   *
   * Called after the role list loads, because the Permission Group default has to
   * match an option that actually exists.
   */
  private applySupportUserAccountDefaults() {
    if (!this.isSupportUser || !this.innerFormGroup) {
      return;
    }

    if (!this.innerFormGroup.get("department").value) {
      this.innerFormGroup
        .get("department")
        .setValue(SUPPORT_USER_DEFAULT_DEPARTMENT);
    }

    if (!this.innerFormGroup.get("position").value) {
      this.innerFormGroup
        .get("position")
        .setValue(SUPPORT_USER_DEFAULT_POSITION);
    }

    const disciplines = this.innerFormGroup.get("employeeAccountDisciplines");
    if (disciplines && (!disciplines.value || disciplines.value.length === 0)) {
      disciplines.setValue([SUPPORT_USER_DEFAULT_DISCIPLINE]);
    }

    const roles = this.innerFormGroup.get("employeeAccountDetails");
    if (roles && (!roles.value || roles.value.length === 0)) {
      // Match against the loaded options rather than pushing a bare id, so the
      // multi-select renders it and the save carries the role NAME too — OrgRoles
      // stores {id, name} and the name is denormalised into UserProfile.
      const administrator = (this.rolesData || []).find(
        (role: any) => role && role.name === SUPPORT_USER_DEFAULT_ROLE_NAME
      );
      if (administrator) {
        roles.setValue([administrator]);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/workforce-account-contact/workforce-account-contact-details.ts